import {
  ConfirmDialog,
  customAlertApiRef,
  WbButton,
  WbLink,
} from '@agilelab/plugin-wb-platform';
import { builderDpCommitPermission } from '@agilelab/plugin-wb-rbac-common';
import { stringifyEntityRef } from '@backstage/catalog-model';
import { useApi, useRouteRef } from '@backstage/core-plugin-api';
import { usePermission } from '@backstage/plugin-permission-react';
import { Tooltip, Typography } from '@material-ui/core';
import { FC, useState } from 'react';
import useAsyncFn from 'react-use/lib/useAsyncFn';
import { panelCatalogApiRef } from '../../api';
import { releaseDetailRouteRef } from '../../routes';
import { generateUpdateTooltip, getReleaseInfo } from '../../utils';
import { useEditorPageContext } from './context/useEditorPageContext';

interface Props {
  variant?: 'text' | 'outlined' | 'contained' | undefined;
}

export const UpdateButton: FC<Props> = ({ variant }) => {
  const [openDialog, setOpenDialog] = useState(false);
  const {
    entity,
    setVersion,
    addRelease,
    entitiesAndBranches,
    setCurrRelease,
    actionError,
    processorErrors,
    oldDescriptorFound,
  } = useEditorPageContext();
  const panelCatalog = useApi(panelCatalogApiRef);
  const alertApi = useApi(customAlertApiRef);

  const releaseDetailRoute = useRouteRef(releaseDetailRouteRef);

  const { allowed: canCommit } = usePermission({
    permission: builderDpCommitPermission,
    resourceRef: stringifyEntityRef({
      kind: entity?.kind ?? 'system',
      namespace: 'default',
      name: entity?.metadata.name ?? '',
    }),
  });

  const [shapshotState, commitSnapshot] = useAsyncFn(async () => {
    try {
      const entities = Object.entries(entitiesAndBranches || {}).map(
        ([key, value]) => ({ entityRef: key, branch: value }),
      );
      const newRelease = await panelCatalog.commitSnapshot(
        entity.metadata.name,
        entity.kind,
        entities,
      );

      // FIXME: This is a hack to force the descriptor to be re-fetched
      const url = new URL(window.location.href);
      url.searchParams.set('bypassCache', 'true');
      window.history.replaceState(window.history.state, '', url);

      setVersion?.(newRelease.metadata.version);
      setCurrRelease(newRelease);
      addRelease(newRelease);

      const { kind, namespace, name } = getReleaseInfo(newRelease);

      alertApi.post({
        actions: (
          <WbLink
            route={releaseDetailRoute({ kind, namespace, name })}
            text={`Open V${newRelease?.metadata?.version}`}
          />
        ),
        message: 'Draft-Release updated.',
        severity: 'success',
      });
    } catch (error) {
      alertApi.post({
        error,
        severity: 'error',
      });
    } finally {
      setOpenDialog(false);
    }
  }, [
    alertApi,
    entity.metadata.name,
    panelCatalog,
    setVersion,
    entitiesAndBranches,
  ]);

  return (
    <>
      <Tooltip
        title={generateUpdateTooltip(
          canCommit,
          actionError,
          false,
          false,
          processorErrors,
        )}
      >
        <span>
          <WbButton
            label="Update draft release"
            color="primary"
            size="small"
            variant={variant}
            disabled={!canCommit || !!actionError || !!processorErrors?.length}
            onClick={() => setOpenDialog(true)}
          />
        </span>
      </Tooltip>
      <ConfirmDialog
        title="A draft release already exists!"
        description={
          <>
            <Typography>
              By updating you will lose the current draft release and a new one
              will be created.
            </Typography>
            <Typography style={{ fontWeight: '700' }}>
              Are you sure you want to proceed with the update?
            </Typography>
            {oldDescriptorFound && (
              <>
                <br />
                <Typography style={{ fontWeight: '700' }}>Note: </Typography>
                <Typography>
                  A new version of descriptor have been found. The release will
                  be created from the last version (that could differ from the
                  one you see here). Click on "Refresh" to see the last version
                  of the descriptor.
                </Typography>
              </>
            )}
          </>
        }
        open={openDialog}
        onConfirm={commitSnapshot}
        onClose={() => setOpenDialog(false)}
        inProgress={shapshotState.loading}
        confirmButtonText="Yes, update"
      />
    </>
  );
};
